// Helpers de baixo nível para o write-back textual ao STEP (ISO
// 10303-21), partilhados pelos patchers (georef, placement de produtos,
// atributos, psets). Não fazem IO nem conhecem semântica IFC: só
// manipulam o texto do ficheiro. Cobertos por testes unitários
// (src/viewer/lib/__tests__/stepHelpers.test.ts e stepWrite.test.ts).
//
// Nota: os patchers de atributos/psets (ifc-edit) têm um
// `splitTopLevelArgs` PRÓPRIO que devolve offsets (Span[]) em vez de
// strings; esse é uma função diferente e fica onde está.

const CHUNK = 0x8000

/** Uint8Array -> string latin1 (1 byte por caractere). Usar sempre que
 *  se lê bytes de IFC para manipulação textual: preserva os bytes
 *  originais sem interpretação UTF-8. */
export function bytesToLatin1(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK)),
    )
  }
  return out
}

/** string latin1 -> Uint8Array (trunca cada caractere a 1 byte). Mirror
 *  de bytesToLatin1 para reescrever o ficheiro. */
export function latin1ToBytes(text: string): Uint8Array {
  const out = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) {
    out[i] = text.charCodeAt(i) & 0xff
  }
  return out
}

export interface StepEntity {
  /** Índice do '#' inicial. */
  start: number
  /** Índice EXCLUSIVO logo a seguir ao ';' que fecha a entidade (sem
   *  incluir eventual newline). */
  end: number
  /** Tipo em MAIÚSCULAS (ex.: "IFCMAPCONVERSION"). */
  type: string
  /** Texto cru entre os parênteses exteriores (sem os parênteses). */
  args: string
}

/** Núcleo partilhado: a partir do início de uma entidade (índice do '#',
 *  o tipo já conhecido e o índice logo após o '(' de abertura), percorre
 *  com consciência de strings (plicas, com '' escapado) e de parênteses
 *  aninhados até ao ')' de fecho e depois ao ';'. Devolve null quando o
 *  ';' nunca fecha (ficheiro truncado). */
function scanEntity(
  text: string,
  start: number,
  type: string,
  argsStart: number,
): StepEntity | null {
  let i = argsStart
  let depth = 1
  let inString = false
  while (i < text.length) {
    const ch = text[i]
    if (inString) {
      if (ch === "'") {
        if (text[i + 1] === "'") i++ // plica escapada ''
        else inString = false
      }
    } else if (ch === "'") {
      inString = true
    } else if (ch === '(') {
      depth++
    } else if (ch === ')') {
      depth--
      if (depth === 0) {
        let j = i + 1
        while (j < text.length && text[j] !== ';') j++
        if (j >= text.length) return null
        return { start, end: j + 1, type, args: text.slice(argsStart, i) }
      }
    }
    i++
  }
  return null
}

/** Primeira entidade `#id=TYPE(...)` do tipo dado (procura por tipo).
 *  `type` tem de ser um nome de entidade simples em maiúsculas; o `(`
 *  imediatamente a seguir garante que "IFCMAPCONVERSION" não apanha
 *  "IFCMAPCONVERSIONSCALED". null quando não existe. */
export function findEntityByType(
  text: string,
  type: string,
): StepEntity | null {
  const re = new RegExp(`#\\d+\\s*=\\s*${type}\\s*\\(`, 'g')
  const m = re.exec(text)
  if (!m) return null
  return scanEntity(text, m.index, type, m.index + m[0].length)
}

/** Entidade `#id=TYPE(...)` pelo expressID. O `=` logo a seguir aos
 *  dígitos garante que #22 não apanha #220. null quando não existe. */
export function findEntityById(text: string, id: number): StepEntity | null {
  const re = new RegExp(`#${id}\\s*=\\s*([A-Z0-9]+)\\s*\\(`, 'g')
  const m = re.exec(text)
  if (!m) return null
  return scanEntity(text, m.index, m[1], m.index + m[0].length)
}

/** Divide a lista de argumentos de uma entidade STEP pelos separadores
 *  de topo (vírgulas fora de strings e de parênteses aninhados). */
export function splitTopLevelArgs(argsText: string): string[] {
  const out: string[] = []
  let depth = 0
  let inString = false
  let cur = ''
  for (let i = 0; i < argsText.length; i++) {
    const ch = argsText[i]
    if (inString) {
      cur += ch
      if (ch === "'") {
        if (argsText[i + 1] === "'") {
          cur += "'"
          i++
        } else {
          inString = false
        }
      }
      continue
    }
    if (ch === "'") {
      inString = true
      cur += ch
    } else if (ch === '(') {
      depth++
      cur += ch
    } else if (ch === ')') {
      depth--
      cur += ch
    } else if (ch === ',' && depth === 0) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out
}

/** Formata um número como REAL do STEP (tem sempre ponto decimal, sem
 *  notação exponencial). Lança para valores não finitos. */
export function stepReal(n: number): string {
  if (!Number.isFinite(n)) {
    throw new Error('Valor numérico inválido para o STEP.')
  }
  let s = String(n)
  if (s.includes('e') || s.includes('E')) {
    s = n.toFixed(12).replace(/0+$/, '')
    if (s.endsWith('.')) s += '0'
  }
  if (!s.includes('.')) s += '.'
  return s
}
